import {
  ChangeDetectorRef,
  Component,
  inject,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  FormsModule,
  NgForm,
} from '@angular/forms';
import {
  Observable,
  Subject,
  finalize,
  of,
  takeUntil,
} from 'rxjs';
import {
  debounceTime,
  distinctUntilChanged,
} from 'rxjs/operators';
import {
  Router,
  RouterModule,
} from '@angular/router';
import { HttpErrorResponse } from '@angular/common/http';

import { WorkOrderNumbersService } from './work-order-numbers.service';
import {
  CreateWorkOrderNumberPayload,
  UpdateWorkOrderNumberPayload,
  WorkOrderNumber,
  WorkOrderNumberFilters,
} from '../models/work-order-numbers.model';
import { PlanningTopNavComponent } from '../../../shared/planning-nav/planning-top-nav.component';
import { FilterService } from '../../../services/filter.service';

type WorkOrderFormModel = {
  won: string;
  project: string | null;
  departmentCode: string;
  description: string;
  isActive: 'true' | 'false';
  isOverhead: boolean;
};

interface WorkOrderFiltersVm {
  departmentIds: number[] | null;
  projectId: number | null;
  isOverhead: boolean | null;
  searchTerm: string;
}

@Component({
  selector: 'app-work-orders',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    PlanningTopNavComponent,
    RouterModule,
  ],
  templateUrl:
    './work-order-numbers.component.html',
  styleUrls: [
    './work-order-numbers.component.scss',
  ],
})
export class WorkOrdersComponent
  implements OnInit, OnDestroy
{
  workOrders$: Observable<
    WorkOrderNumber[]
  > = of([]);

  isLoading = false;
  errorMessage: string | null = null;

  readonly filterService =
    inject(FilterService);

  currentPage = 1;
  totalPages = 1;
  totalCount = 0;
  pageSize = 20;
  pageSizes = [20, 50, 100];

  filters: WorkOrderFiltersVm = {
    departmentIds: [],
    projectId: null,
    isOverhead: null,
    searchTerm: '',
  };

  searchInputValue = '';

  modalOpen = false;
  isEditMode = false;

  currentWorkOrder:
    | WorkOrderNumber
    | null = null;

  formModel: WorkOrderFormModel =
    this.getEmptyForm();

  deleteConfirmOpen = false;

  workOrderToDelete:
    | WorkOrderNumber
    | null = null;

  toastVisible = false;
  toastMessage = '';
  toastType:
    | 'success'
    | 'error' = 'success';

  private readonly searchInput$ =
    new Subject<string>();

  private readonly destroy$ =
    new Subject<void>();

  private loadRequestId = 0;

  constructor(
    private readonly workOrdersService:
      WorkOrderNumbersService,
    private readonly cd:
      ChangeDetectorRef,
    private readonly router: Router,
  ) {}

  ngOnInit(): void {
    this.bindSearch();
    this.loadInitialData();
  }

  ngOnDestroy(): void {
    this.searchInput$.complete();
    this.destroy$.next();
    this.destroy$.complete();

    this.unlockBodyScroll();
  }

  private lockBodyScroll(): void {
    document.body.style.overflow =
      'hidden';
  }

  private unlockBodyScroll(): void {
    document.body.style.overflow = '';
  }

  private syncBodyScrollLock(): void {
    if (
      this.modalOpen ||
      this.deleteConfirmOpen
    ) {
      this.lockBodyScroll();
      return;
    }

    this.unlockBodyScroll();
  }

  get hasActiveFilters(): boolean {
    return (
      (
        this.filters.departmentIds
          ?.length ?? 0
      ) > 0 ||
      this.filters.projectId !== null ||
      this.filters.isOverhead !== null ||
      !!this.filters.searchTerm.trim()
    );
  }

  get selectedDepartmentId():
    | number
    | null {
    return this.filters.departmentIds
      ?.length
      ? this.filters.departmentIds[0]
      : null;
  }

  set selectedDepartmentId(
    value: number | null,
  ) {
    this.filters.departmentIds =
      value !== null
        ? [value]
        : null;
  }

  loadInitialData(): void {
    this.isLoading = true;
    this.cd.detectChanges();

    this.loadWorkOrders(1);
  }

  private bindSearch(): void {
    this.searchInput$
      .pipe(
        debounceTime(1000),
        distinctUntilChanged(),
        takeUntil(this.destroy$),
      )
      .subscribe((value) => {
        this.filters.searchTerm = value;
        this.loadWorkOrders(1);
      });
  }

  onDepartmentChange(
    value:
      | number
      | string
      | null,
  ): void {
    const departmentId =
      this.toNullableNumber(value);

    this.filters.departmentIds =
      departmentId !== null
        ? [departmentId]
        : null;

    this.loadWorkOrders(1);
  }

  onProjectChange(
    value:
      | number
      | string
      | null,
  ): void {
    this.filters.projectId =
      this.toNullableNumber(value);

    this.loadWorkOrders(1);
  }

  onIsOverheadChange(
    value:
      | string
      | boolean
      | null,
  ): void {
    if (
      value === null ||
      value === '' ||
      value === 'all'
    ) {
      this.filters.isOverhead = null;
    } else if (
      value === true ||
      value === 'true'
    ) {
      this.filters.isOverhead = true;
    } else if (
      value === false ||
      value === 'false'
    ) {
      this.filters.isOverhead = false;
    } else {
      this.filters.isOverhead = null;
    }

    this.loadWorkOrders(1);
  }

  onSearchTermChange(
    value: string | null,
  ): void {
    const normalized = String(
      value ?? '',
    ).trimStart();

    this.searchInputValue = normalized;
    this.searchInput$.next(normalized);
  }

  clearFilters(): void {
    this.filters = {
      departmentIds: [],
      projectId: null,
      isOverhead: null,
      searchTerm: '',
    };

    this.searchInputValue = '';
    this.loadWorkOrders(1);
  }

  private buildFilters():
    WorkOrderNumberFilters {
    return {
      departmentIds:
        this.filters.departmentIds &&
        this.filters.departmentIds
          .length > 0
          ? this.filters.departmentIds
          : null,

      projectId:
        this.filters.projectId,

      isOverhead:
        this.filters.isOverhead,

      searchTerm:
        this.filters.searchTerm
          .trim() || null,
    };
  }

  private toNullableNumber(
    value:
      | number
      | string
      | null
      | undefined,
  ): number | null {
    if (
      value === null ||
      value === undefined ||
      value === ''
    ) {
      return null;
    }

    const parsed = Number(value);

    return (
      Number.isFinite(parsed) &&
      parsed > 0
        ? parsed
        : null
    );
  }

  loadWorkOrders(
    page = this.currentPage,
  ): void {
    const requestId =
      ++this.loadRequestId;

    this.isLoading = true;
    this.errorMessage = null;
    this.cd.detectChanges();

    this.workOrdersService
      .getWorkOrderNumbers(
        page,
        this.pageSize,
        this.buildFilters(),
      )
      .pipe(
        finalize(() => {
          if (
            requestId ===
            this.loadRequestId
          ) {
            this.isLoading = false;
            this.cd.detectChanges();
          }
        }),
        takeUntil(this.destroy$),
      )
      .subscribe({
        next: (response) => {
          if (
            requestId !==
            this.loadRequestId
          ) {
            return;
          }

          if (!response) {
            this.workOrders$ = of([]);
            this.totalPages = 1;
            this.totalCount = 0;
            this.currentPage = 1;
            this.errorMessage =
              'Failed to load work orders';

            this.cd.detectChanges();
            return;
          }

          this.totalPages =
            response.totalPages ?? 1;

          this.totalCount =
            response.totalCount ?? 0;

          this.currentPage =
            response.currentPage ?? 1;

          this.workOrders$ = of(
            response.data ?? [],
          );

          this.cd.detectChanges();
        },
        error: () => {
          if (
            requestId !==
            this.loadRequestId
          ) {
            return;
          }

          this.errorMessage =
            'Failed to load work orders';

          this.workOrders$ = of([]);
          this.totalPages = 1;
          this.totalCount = 0;
          this.currentPage = 1;

          this.cd.detectChanges();
        },
      });
  }

  changePage(page: number): void {
    if (
      page < 1 ||
      page > this.totalPages
    ) {
      return;
    }

    this.currentPage = page;

    this.loadWorkOrders(
      this.currentPage,
    );
  }

  changePageSize(size: number): void {
    this.pageSize = Number(size);
    this.currentPage = 1;

    this.loadWorkOrders(1);
  }

  openCreateModal(): void {
    this.isEditMode = false;
    this.currentWorkOrder = null;
    this.formModel =
      this.getEmptyForm();

    this.modalOpen = true;
    this.syncBodyScrollLock();

    this.cd.detectChanges();
  }

  openEditModal(
    workOrder: WorkOrderNumber,
  ): void {
    this.isEditMode = true;
    this.currentWorkOrder = workOrder;

    this.formModel = {
      won: workOrder.won,

      project:
        workOrder.project?.name ??
        null,

      departmentCode: String(
        workOrder.department.code,
      ),

      description:
        workOrder.description ?? '',

      isActive:
        workOrder.isActive
          ? 'true'
          : 'false',

      isOverhead:
        workOrder.isOverhead ??
        false,
    };

    this.modalOpen = true;
    this.syncBodyScrollLock();

    this.cd.detectChanges();
  }

  closeModal(): void {
    this.modalOpen = false;
    this.isEditMode = false;
    this.currentWorkOrder = null;

    this.formModel =
      this.getEmptyForm();

    this.syncBodyScrollLock();

    this.cd.detectChanges();
  }

  submitWorkOrder(
    form?: NgForm,
  ): void {
    if (form?.invalid) {
      form.control.markAllAsTouched();
      return;
    }

    if (
      this.isEditMode &&
      this.currentWorkOrder
    ) {
      this.updateWorkOrder();
      return;
    }

    this.createWorkOrder();
  }

  private createWorkOrder(): void {
    const payload:
      CreateWorkOrderNumberPayload =
      this.buildPayload();

    this.workOrdersService
      .createWorkOrderNumber(payload)
      .pipe(
        takeUntil(this.destroy$),
      )
      .subscribe({
        next: () => {
          this.closeModal();

          this.showToast(
            'Work order created successfully',
            'success',
          );

          this.loadWorkOrders(1);
        },
        error: (
          error: HttpErrorResponse,
        ) => {
          console.error(
            'Failed to create work order:',
            {
              status: error.status,
              response: error.error,
              payload,
            },
          );

          this.showToast(
            this.getApiErrorMessage(
              error,
              'Failed to create work order',
            ),
            'error',
          );
        },
      });
  }

  private updateWorkOrder(): void {
    if (!this.currentWorkOrder) {
      return;
    }

    const payload:
      UpdateWorkOrderNumberPayload =
      this.buildPayload();

    this.workOrdersService
      .updateWorkOrderNumber(
        this.currentWorkOrder.id,
        payload,
      )
      .pipe(
        takeUntil(this.destroy$),
      )
      .subscribe({
        next: () => {
          const currentPage =
            this.currentPage;

          this.closeModal();

          this.showToast(
            'Work order updated successfully',
            'success',
          );

          this.loadWorkOrders(
            currentPage,
          );
        },
        error: (
          error: HttpErrorResponse,
        ) => {
          console.error(
            'Failed to update work order:',
            {
              status: error.status,
              response: error.error,
              payload,
            },
          );

          this.showToast(
            this.getApiErrorMessage(
              error,
              'Failed to update work order',
            ),
            'error',
          );
        },
      });
  }

  private buildPayload():
    CreateWorkOrderNumberPayload &
    UpdateWorkOrderNumberPayload {
    return {
      won: this.formModel.won.trim(),

      project:
        this.formModel.project,

      departmentCode:
        this.formModel
          .departmentCode
          .trim(),

      description:
        this.formModel
          .description
          .trim(),

      isActive:
        this.formModel.isActive ===
        'true',

      isOverhead:
        this.formModel.isOverhead,
    };
  }

  private getApiErrorMessage(
    error: HttpErrorResponse,
    fallbackMessage: string,
  ): string {
    const response = error.error;

    if (
      typeof response === 'string' &&
      response.trim()
    ) {
      return response;
    }

    if (
      typeof response?.message ===
        'string' &&
      response.message.trim()
    ) {
      return response.message;
    }

    if (
      typeof response?.title ===
        'string' &&
      response.title.trim()
    ) {
      return response.title;
    }

    const errors = response?.errors;

    if (
      errors &&
      typeof errors === 'object'
    ) {
      const firstError =
        Object.values(errors)
          .flatMap((value) =>
            Array.isArray(value)
              ? value
              : [value],
          )
          .find(
            (value) =>
              typeof value ===
                'string' &&
              value.trim(),
          );

      if (
        typeof firstError ===
        'string'
      ) {
        return firstError;
      }
    }

    return fallbackMessage;
  }

  showToast(
    message: string,
    type:
      | 'success'
      | 'error',
  ): void {
    this.toastMessage = message;
    this.toastType = type;
    this.toastVisible = true;

    this.cd.detectChanges();

    window.setTimeout(() => {
      this.toastVisible = false;
      this.cd.markForCheck();
    }, 2500);
  }

  private getEmptyForm():
    WorkOrderFormModel {
    return {
      won: '',
      project: null,
      departmentCode: '',
      description: '',
      isActive: 'true',
      isOverhead: false,
    };
  }

  goToImport(): void {
    this.router.navigate([
      '/settings/planning/work-orders/import',
    ]);
  }

  goToAssign(): void {
    this.router.navigate([
      '/settings/planning/work-orders/assign',
    ]);
  }
}